import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSongs } from '@/context/SongContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import FragmentCard from '@/components/FragmentCard';
import AddFragmentDrawer from '@/components/AddFragmentDrawer';
import ExportDialog from '@/components/ExportDialog';
import { ArrowLeft, Plus, Download, MoreVertical, ChevronUp, ChevronDown } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function SongPage() {
    const { songId } = useParams();
    const navigate = useNavigate();
    const { currentSong, loadSong, updateSongTitle, deleteSong, reorderFragments } = useSongs();
    const [loading, setLoading] = useState(true);
    const [title, setTitle] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [exportOpen, setExportOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [titleFocused, setTitleFocused] = useState(false);
    const [newFragmentId, setNewFragmentId] = useState(null);
    const [fabAnimating, setFabAnimating] = useState(false);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            const song = await loadSong(songId);
            if (song) {
                setTitle(song.title || '');
            }
            setLoading(false);
        };
        load();
    }, [songId, loadSong]);

    // Clear new fragment highlight after animation
    useEffect(() => {
        if (newFragmentId) {
            const timer = setTimeout(() => setNewFragmentId(null), 500);
            return () => clearTimeout(timer);
        }
    }, [newFragmentId]);

    const handleTitleChange = (e) => {
        setTitle(e.target.value);
    };

    const handleTitleBlur = () => {
        setTitleFocused(false);
        if (title !== currentSong?.title) {
            updateSongTitle(songId, title);
        }
    };

    const handleDeleteSong = async () => {
        await deleteSong(songId);
        navigate('/');
    };

    const handleOpenDrawer = () => {
        setFabAnimating(true);
        setTimeout(() => setFabAnimating(false), 300);
        setDrawerOpen(true);
    };

    const handleFragmentAdded = (fragmentId) => {
        setNewFragmentId(fragmentId);
    };

    // Move fragment up
    const moveUp = useCallback(async (index) => {
        if (index > 0 && reorderFragments) {
            await reorderFragments(songId, index, index - 1);
        }
    }, [songId, reorderFragments]);

    // Move fragment down
    const moveDown = useCallback(async (index) => {
        if (currentSong && index < currentSong.fragments.length - 1 && reorderFragments) {
            await reorderFragments(songId, index, index + 1);
        }
    }, [songId, currentSong, reorderFragments]);

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
                    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4">
                        <Skeleton className="h-8 w-48" />
                    </div>
                </header>
                <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
                    <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                            <Skeleton key={i} className="h-32 w-full rounded-xl" />
                        ))}
                    </div>
                </main>
            </div>
        );
    }

    if (!currentSong) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center animate-fade-in">
                    <h2 className="text-xl font-display text-foreground mb-2">Song not found</h2>
                    <p className="text-muted-foreground mb-4">This song may have been deleted.</p>
                    <Button onClick={() => navigate('/')} variant="outline">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to songs
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background pb-28">
            {/* Header */}
            <header className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate('/')}
                                className="shrink-0 hover:scale-105 transition-transform"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </Button>
                            <Input
                                value={title}
                                onChange={handleTitleChange}
                                onFocus={() => setTitleFocused(true)}
                                onBlur={handleTitleBlur}
                                placeholder="Untitled Song"
                                className={`font-display text-xl border-0 bg-transparent px-2 h-auto py-1 focus-visible:ring-0 focus-visible:ring-offset-0 transition-colors ${
                                    titleFocused ? 'bg-muted/50' : ''
                                }`}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setExportOpen(true)}
                                className="text-muted-foreground hover:text-foreground hover:scale-105 transition-transform"
                            >
                                <Download className="w-5 h-5" />
                            </Button>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="hover:scale-105 transition-transform">
                                        <MoreVertical className="w-5 h-5" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                        onClick={() => setExportOpen(true)}
                                        className="gap-2"
                                    >
                                        <Download className="w-4 h-4" />
                                        Export song
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onClick={() => setDeleteOpen(true)}
                                        className="gap-2 text-destructive focus:text-destructive"
                                    >
                                        Delete song
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </div>
            </header>

            {/* Fragment Feed */}
            <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
                {currentSong.fragments.length === 0 ? (
                    <div className="text-center py-16 animate-fade-in">
                        <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
                            <Plus className="w-8 h-8 text-muted-foreground" />
                        </div>
                        <h2 className="text-lg font-display text-foreground mb-2">
                            Start adding fragments
                        </h2>
                        <p className="text-muted-foreground text-sm max-w-xs mx-auto leading-relaxed">
                            Record audio, write lyrics, tap a tempo, or jot down instrument notes.
                            Build your song piece by piece.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4 pl-12">
                        {currentSong.fragments.map((fragment, index) => (
                            <div
                                key={fragment.id}
                                className={`group/frag relative ${
                                    newFragmentId === fragment.id ? 'fragment-new' : 'animate-fade-in'
                                }`}
                                style={{ 
                                    animationDelay: `${index * 60}ms`
                                }}
                            >
                                {/* Reorder buttons - left side */}
                                <div className="absolute -left-11 top-1/2 -translate-y-1/2 flex flex-col gap-0.5">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => moveUp(index)}
                                        disabled={index === 0}
                                        className="h-8 w-8 rounded-full hover:bg-muted disabled:opacity-20"
                                        title="Move up"
                                    >
                                        <ChevronUp className="w-5 h-5" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => moveDown(index)}
                                        disabled={index === currentSong.fragments.length - 1}
                                        className="h-8 w-8 rounded-full hover:bg-muted disabled:opacity-20"
                                        title="Move down"
                                    >
                                        <ChevronDown className="w-5 h-5" />
                                    </Button>
                                </div>
                                <FragmentCard
                                    fragment={fragment}
                                    songId={songId}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </main>

            {/* Floating Add Button - Enhanced */}
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-20">
                <Button
                    onClick={handleOpenDrawer}
                    className={`h-16 px-8 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-float hover:shadow-lg hover:scale-105 active:scale-95 transition-all duration-normal ${
                        fabAnimating ? 'animate-pop' : ''
                    }`}
                >
                    <Plus className="w-6 h-6 mr-2" />
                    <span className="text-base font-medium">Add Fragment</span>
                </Button>
            </div>

            {/* Add Fragment Drawer */}
            <AddFragmentDrawer
                open={drawerOpen}
                onOpenChange={setDrawerOpen}
                songId={songId}
                onFragmentAdded={handleFragmentAdded}
            />

            {/* Export Dialog */}
            <ExportDialog
                open={exportOpen}
                onOpenChange={setExportOpen}
                song={currentSong}
            />

            {/* Delete Confirmation */}
            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialogContent className="max-w-md animate-scale-in">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display text-xl">
                            Delete this song?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            &quot;{currentSong?.title || 'Untitled Song'}&quot; will be permanently removed
                            from your device. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Keep it</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteSong}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}